import React, { useContext } from 'react'

import {
  useForm, useLatestEntity, useSetState,
} from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { digObject, launchModal } from '@domain-group/javascript-utils'

import { mergeAndStringifyEntityObjects } from '@functions/entity'

import useDispatch from '@hooks/useDispatch'
import useSelector from '@hooks/useSelector'

import * as attachmentActions from '@redux/modules/attachment'
import { CopyParams } from '@redux/modules/attachment'

import defaultFormState, { requiredFields } from '@models/attachment'

import PageContext from '@contexts/pageContext'

import { getAttachmentType } from '@functions/attachment'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'

import type { AttachmentModel, AttachmentRequestOptions, OrderItemModel } from '@models/types'

import useReduxAction from './useReduxAction'

const stringifyObjectKeys = ['data']

type CreateAttachmentParams = {
  attachmentParams: Partial<AttachmentModel>,
  dispatch: AppDispatch,
  requestOptions?: AttachmentRequestOptions,
}

const createAttachment = (params: CreateAttachmentParams) => {
  const { dispatch, attachmentParams, requestOptions } = params
  const { createAttachment: createFn } = attachmentActions

  // @ts-expect-error TS2345
  const updatedParams = mergeAndStringifyEntityObjects({}, attachmentParams, stringifyObjectKeys)

  return dispatch(createFn(updatedParams, requestOptions))
}

type UpdateAttachmentParams = {
  attachment: AttachmentModel,
  attachmentParams: Partial<AttachmentModel>,
  dispatch: AppDispatch,
  requestOptions?: AttachmentRequestOptions,
}

const updateAttachment = (params: UpdateAttachmentParams) => {
  const {
    dispatch, attachment, attachmentParams, requestOptions,
  } = params
  const { updateAttachment: updateFn } = attachmentActions

  // @ts-expect-error TS2345
  const updatedParams = mergeAndStringifyEntityObjects(attachment, attachmentParams, stringifyObjectKeys)

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteAttachmentParams = {
  attachment: DeleteParams<AttachmentModel>,
  dispatch: AppDispatch,
}

const deleteAttachment = (params: DeleteAttachmentParams) => {
  const { dispatch, attachment } = params
  const { deleteAttachment: deleteFn } = attachmentActions

  return dispatch(deleteFn(attachment))
}

type DefaultState = {
  generating: boolean,
}

type SetState = (state: Partial<DefaultState>) => void

type DownloadAttachmentParams = {
  attachment: AttachmentModel,
  dispatch: AppDispatch,
  e: React.MouseEvent<HTMLButtonElement>,
  setState: SetState,
}

const downloadAttachment = (params: DownloadAttachmentParams) => {
  const {
    attachment, dispatch, e, setState,
  } = params

  e.stopPropagation()
  const attachmentType = getAttachmentType(attachment)
  const { generateAttachmentUrl } = attachmentActions

  if (attachmentType === 'image'){
    const downloadUrl = digObject(attachment, 'links.original_url')
    window.open(downloadUrl, '_blank')
    return
  }

  setState({ generating: true })

  // @ts-expect-error TS2339
  dispatch(generateAttachmentUrl(attachment.id)).then(({ success, data }) => {
    // @ts-expect-error TS2339
    if (success && data.links?.force_attachment){
      // @ts-expect-error TS2339
      window.location.href = data.links.force_attachment
    }

    setState({ generating: false })
  })
}

type PrepareDownloadParams = {
  attachmentIds: number[],
  dispatch: AppDispatch,
  orderItemId: number,
}

const prepareDownload = (params: PrepareDownloadParams) => {
  const { attachmentIds, dispatch, orderItemId } = params

  const { prepareDownload: downloadFn } = attachmentActions

  const updatedParams = {
    order_item_id: orderItemId,
    attachment_ids: attachmentIds,
  }

  return dispatch(downloadFn(updatedParams))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useAttachmentForm(
  attachment: Partial<AttachmentModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const attachmentForm = useForm(
    defaultFormState,
    { entity: attachment, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [attachment.id, attachment.cache_key],
  )

  return {
    ...attachmentForm,
  }
}

type CopyToCampaignParams = {
  copyParams: CopyParams,
  dispatch: AppDispatch,
  requestOptions?: AttachmentRequestOptions,
}

const copyToCampaign = (params: CopyToCampaignParams) => {
  const {
    copyParams, dispatch, requestOptions,
  } = params
  const { copyToCampaign: copyFn } = attachmentActions

  return dispatch(copyFn(copyParams, requestOptions))
}

const defaultState: DefaultState = {
  generating: false,
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: AttachmentRequestOptions,
}

function useAttachment(initEntity: Partial<AttachmentModel> = {}, options: Options = {}) {
  const {
    performHttpRequests, requestOptions,
  } = options

  // @ts-expect-error TS2322
  const { entity: attachment }: { entity: AttachmentModel } = useLatestEntity(initEntity, 'attachments')
  const dispatch = useDispatch()
  const { id } = attachment

  const [state, setState] = useSetState(defaultState)
  const { generating } = state

  const { callbacks } = useContext(PageContext)

  const {
    creating, deleting, updating,
  } = useSelector(reduxState => reduxState.attachments)

  const attachmentType = getAttachmentType(attachment)

  useReduxAction(
    'attachments',
    'loadAttachment',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  return {
    attachment,
    attachmentType,
    callbacks: {
      copyToCampaign: (copyParams: CopyParams, entityOptions?: AttachmentRequestOptions) => (
        copyToCampaign({
          copyParams, dispatch, requestOptions: entityOptions,
        })
      ),
      createAttachment: (attachmentParams: Partial<AttachmentModel>, entityOptions?: AttachmentRequestOptions) => (
        createAttachment({ attachmentParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditAttachment: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditAttachmentModal',
        payload: { attachment },
      }),
      createOrEditProviderAttachment: (providerImageParams: {
        orderItem: OrderItemModel,
        campaignTitle: string,
       }) => launchModal({
        callbacks,
        modalKey: 'CreateOrEditProviderAttachmentModal',
        payload: { attachment, ...providerImageParams },
      }),
      deleteAttachment: () => deleteAttachment({ dispatch, attachment }),
      downloadAttachment: (e: React.MouseEvent<HTMLButtonElement>) => downloadAttachment({
        e, attachment, setState, dispatch,
      }),
      prepareDownload: (orderItemId: number, attachmentIds: number[]) => (
        prepareDownload({ attachmentIds, dispatch, orderItemId })
      ),
      updateAttachment: (attachmentParams: Partial<AttachmentModel>, entityOptions?: AttachmentRequestOptions) => (
        updateAttachment({
          attachment, attachmentParams, dispatch, requestOptions: entityOptions,
        })
      ),
      uploadAttachment: (attachmentParams: Partial<AttachmentModel>, customPayload?: object) => launchModal({
        callbacks,
        modalKey: 'UploadEntityAssetsModal',
        payload: {
          attachmentParams,
          ...customPayload,
        },
      }),
    },
    creating,
    deleting,
    generating,
    updating,
  }
}

export type UseAttachmentPayload = ReturnType<typeof useAttachment>

export default useAttachment
